/**
 * Migration utilities for converting calibration points from absolute to relative coordinates
 */

import { CalibrationPoint, Ramp } from "../types";

/**
 * Detects if a calibration point is using the old absolute coordinate format
 * Old format: pixel coordinates (typically > 1)
 * New format: relative coordinates (0.0 to 1.0)
 */
export const isOldFormatCalibrationPoint = (
  point: CalibrationPoint
): boolean => {
  // If either coordinate is greater than 1, it's likely the old pixel format
  // We allow some tolerance for edge cases where relative coordinates might be exactly 1.0
  return point.x > 1.01 || point.y > 1.01;
};

/**
 * Converts a calibration point from absolute pixel coordinates to relative coordinates
 */
export const convertAbsoluteToRelative = (
  point: CalibrationPoint,
  canvasWidth: number,
  canvasHeight: number
): CalibrationPoint => {
  return {
    ...point,
    x: point.x / canvasWidth,
    y: point.y / canvasHeight,
  };
};

/**
 * Converts a calibration point from relative coordinates to absolute pixel coordinates
 */
export const convertRelativeToAbsolute = (
  point: CalibrationPoint,
  canvasWidth: number,
  canvasHeight: number
): CalibrationPoint => {
  return {
    ...point,
    x: point.x * canvasWidth,
    y: point.y * canvasHeight,
  };
};

/**
 * Migrates calibration points from absolute to relative coordinates for a ramp
 * This function calculates canvas dimensions based on the ramp's reference image dimensions
 */
export const migrateRampCalibrationPoints = async (
  ramp: Ramp
): Promise<CalibrationPoint[]> => {
  if (!ramp.calibration_points || ramp.calibration_points.length === 0) {
    return [];
  }

  // Get canvas dimensions from the reference image
  const canvasDimensions = await getCanvasDimensionsFromRamp(ramp);
  if (!canvasDimensions) {
    console.warn("Could not determine canvas dimensions for ramp", ramp.id);
    return ramp.calibration_points;
  }

  const { width, height } = canvasDimensions;
  const migratedPoints: CalibrationPoint[] = [];

  for (const point of ramp.calibration_points) {
    if (isOldFormatCalibrationPoint(point)) {
      console.log(
        `Migrating calibration point ${point.id} from absolute (${point.x}, ${point.y}) to relative coordinates`
      );
      const migratedPoint = convertAbsoluteToRelative(point, width, height);
      migratedPoints.push(migratedPoint);
    } else {
      // Already in relative format
      migratedPoints.push(point);
    }
  }

  return migratedPoints;
};

/**
 * Get canvas dimensions from a ramp's reference image
 */
export const getCanvasDimensionsFromRamp = (
  ramp: Ramp
): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    if (!ramp.reference_image) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      // Use the same canvas sizing logic as in RampCalibrationCanvas
      const maxWidth = 1000;
      const maxHeight = 600;

      let canvasWidth = img.width;
      let canvasHeight = img.height;

      // Scale down if image is too large
      if (canvasWidth > maxWidth || canvasHeight > maxHeight) {
        const widthRatio = maxWidth / canvasWidth;
        const heightRatio = maxHeight / canvasHeight;
        const ratio = Math.min(widthRatio, heightRatio);

        canvasWidth = canvasWidth * ratio;
        canvasHeight = canvasHeight * ratio;
      }

      resolve({ width: canvasWidth, height: canvasHeight });
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = ramp.reference_image;
  });
};

/**
 * Check if a ramp needs calibration point migration
 */
export const rampNeedsMigration = (ramp: Ramp): boolean => {
  if (!ramp.calibration_points || ramp.calibration_points.length === 0) {
    return false;
  }

  return ramp.calibration_points.some(isOldFormatCalibrationPoint);
};

/**
 * Migration statistics
 */
export interface MigrationStats {
  totalPoints: number;
  migratedPoints: number;
  alreadyMigrated: number;
}

/**
 * Get migration statistics for a ramp
 */
export const getMigrationStats = (ramp: Ramp): MigrationStats => {
  const totalPoints = ramp.calibration_points?.length || 0;
  const migratedPoints =
    ramp.calibration_points?.filter(isOldFormatCalibrationPoint).length || 0;
  const alreadyMigrated = totalPoints - migratedPoints;

  return {
    totalPoints,
    migratedPoints,
    alreadyMigrated,
  };
};
